import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  PhotoIcon, 
  VideoCameraIcon, 
  DocumentIcon, 
  XMarkIcon, 
  PlusIcon,
  ArrowUpTrayIcon,
  EyeIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { Button, Card, Badge, Spinner } from '../ui';
import { ObservationMedia } from '../../types/api';
import { useUploadObservationMedia, useDeleteObservationMedia, useFileValidation } from '../../hooks/api';
import { useCanUseFeature } from '../../hooks/useSubscriptionTier';
import { UpgradePrompt } from '../upgrade/UpgradePrompt';

interface MediaUploadProps {
  observationId?: string;
  existingMedia?: ObservationMedia[];
  onMediaUpdate?: (media: ObservationMedia[]) => void;
  onFilesChange?: (files: File[]) => void; // For create mode
  maxFiles?: number;
  disabled?: boolean;
  allowedTypes?: string[];
  maxFileSize?: number; // in MB
}

interface UploadingFile {
  id: string;
  file: File;
  progress: number;
  error?: string;
  preview?: string;
}

const EnhancedMediaUpload: React.FC<MediaUploadProps> = ({
  observationId,
  existingMedia = [],
  onMediaUpdate,
  onFilesChange,
  maxFiles = 5,
  disabled = false,
  allowedTypes = ['image/*', 'video/*', 'application/pdf'],
  maxFileSize = 10, // 10MB
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]); // For create mode
  const [previewMedia, setPreviewMedia] = useState<ObservationMedia | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const uploadMutation = useUploadObservationMedia();
  const deleteMutation = useDeleteObservationMedia();
  const { validateObservationMedia, getMediaType, formatFileSize } = useFileValidation();
  const canUseMedia = useCanUseFeature('media');

  // Notify parent of file changes in create mode
  useEffect(() => {
    if (!observationId && onFilesChange) {
      onFilesChange(pendingFiles);
    }
  }, [pendingFiles, observationId, onFilesChange]);

  const getMediaIcon = (type: string) => {
    if (type.startsWith('image/')) return PhotoIcon;
    if (type.startsWith('video/')) return VideoCameraIcon;
    return DocumentIcon;
  };

  const createPreview = (file: File): Promise<string | null> => {
    return new Promise((resolve) => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.readAsDataURL(file);
      } else {
        resolve(null);
      }
    });
  };

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    if (disabled) return;
    
    const fileArray = Array.from(files);
    const remainingSlots = maxFiles - existingMedia.length - pendingFiles.length;
    
    if (fileArray.length > remainingSlots) {
      // Show error toast via validation hook
      return;
    }

    for (const file of fileArray) {
      const validation = validateObservationMedia(file);
      if (!validation.isValid) {
        continue;
      }

      const fileId = `${file.name}-${Date.now()}`;
      const preview = await createPreview(file);
      
      // If we have an observation ID, upload immediately
      if (observationId) {
        const uploadingFile: UploadingFile = {
          id: fileId,
          file,
          progress: 0,
          preview: preview || undefined,
        };

        setUploadingFiles(prev => [...prev, uploadingFile]);

        try {
          // Upload the file
          const result = await uploadMutation.mutateAsync({
            observationId,
            file,
            caption: file.name,
          });

          // Update progress to 100%
          setUploadingFiles(prev => 
            prev.map(f => f.id === fileId ? { ...f, progress: 100 } : f)
          );

          // Add to existing media
          if (onMediaUpdate) {
            onMediaUpdate([...existingMedia, result]);
          }

          // Remove from uploading files after a short delay
          setTimeout(() => {
            setUploadingFiles(prev => prev.filter(f => f.id !== fileId));
          }, 1000);
        } catch (error) {
          // Update with error
          setUploadingFiles(prev => 
            prev.map(f => 
              f.id === fileId 
                ? { ...f, error: 'Upload failed' }
                : f
            )
          );
        }
      } else {
        // No observation ID yet, store files for later upload
        setPendingFiles(prev => [...prev, file]);
      }
    }
  }, [observationId, existingMedia, pendingFiles, maxFiles, disabled, onMediaUpdate, uploadMutation, validateObservationMedia]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  }, [handleFiles]);

  const handleRemoveMedia = async (mediaId: string) => {
    if (observationId) {
      // Delete from server
      await deleteMutation.mutateAsync(mediaId);
      const updatedMedia = existingMedia.filter(m => m.id !== mediaId);
      if (onMediaUpdate) {
        onMediaUpdate(updatedMedia);
      }
    }
  };

  const handleRemovePendingFile = (index: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handlePreview = (media: ObservationMedia) => {
    setPreviewMedia(media);
  };

  const canUploadMore = existingMedia.length + uploadingFiles.length + pendingFiles.length < maxFiles;

  // Subscription tier gate — Seedling tier cannot upload media.
  if (!canUseMedia) {
    return <UpgradePrompt feature="media" />;
  }

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      {canUploadMore && (
        <div
          className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            dragActive 
              ? 'border-primary-500 bg-primary-50' 
              : 'border-gray-300 hover:border-gray-400'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => !disabled && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={allowedTypes.join(',')}
            onChange={handleFileInput}
            className="hidden"
            disabled={disabled}
          />
          
          <ArrowUpTrayIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-900 mb-2">
            {dragActive ? 'Drop files here' : 'Upload media files'}
          </p>
          <p className="text-sm text-gray-600 mb-4">
            Drag and drop files here, or click to select files
          </p>
          <p className="text-xs text-gray-500">
            Supported: Images, Videos, PDFs • Max {maxFileSize}MB per file • {maxFiles - existingMedia.length - pendingFiles.length} slots remaining
          </p>
        </div>
      )}

      {/* Pending Files (Create Mode) */}
      {!observationId && pendingFiles.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Files to Upload ({pendingFiles.length})
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {pendingFiles.map((file, index) => (
              <Card key={`pending-${index}`} className="p-3">
                <div className="flex items-center space-x-3">
                  {(() => {
                    const Icon = getMediaIcon(file.type);
                    return <Icon className="w-8 h-8 text-gray-400 flex-shrink-0" />;
                  })()}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemovePendingFile(index)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Uploading Files */}
      {uploadingFiles.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Uploading...
          </h4>
          <div className="space-y-2">
            {uploadingFiles.map(file => (
              <Card key={file.id} className="p-3">
                <div className="flex items-center space-x-3">
                  {file.preview ? (
                    <img 
                      src={file.preview} 
                      alt={file.file.name}
                      className="w-12 h-12 object-cover rounded"
                    />
                  ) : (
                    (() => {
                      const Icon = getMediaIcon(file.file.type);
                      return <Icon className="w-12 h-12 text-gray-400" />;
                    })()
                  )}
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {file.file.name}
                    </p>
                    {file.error ? (
                      <p className="text-xs text-red-600">{file.error}</p>
                    ) : (
                      <div className="mt-1">
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div 
                            className="bg-primary-600 h-1.5 rounded-full transition-all duration-300"
                            style={{ width: `${file.progress}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  {file.progress === 100 && !file.error && (
                    <CheckCircleIcon className="w-5 h-5 text-green-500" />
                  )}
                  {file.error && (
                    <ExclamationCircleIcon className="w-5 h-5 text-red-500" />
                  )}
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Existing Media */}
      {existingMedia.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Uploaded Media ({existingMedia.length})
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {existingMedia.map(media => (
              <Card key={media.id} className="overflow-hidden">
                {media.thumbnail_url || media.url ? (
                  <div className="aspect-w-16 aspect-h-9 bg-gray-100">
                    <img 
                      src={media.thumbnail_url || media.url}
                      alt={media.file_name}
                      className="object-cover w-full h-40"
                    />
                  </div>
                ) : (
                  <div className="h-40 bg-gray-100 flex items-center justify-center">
                    {(() => {
                      const Icon = getMediaIcon(media.media_type || '');
                      return <Icon className="w-16 h-16 text-gray-400" />;
                    })()}
                  </div>
                )}
                <div className="p-3">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {media.file_name}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatFileSize(media.file_size || 0)}
                  </p>
                  {media.caption && (
                    <p className="text-xs text-gray-600 mt-2 italic">
                      {media.caption}
                    </p>
                  )}
                  <div className="flex items-center justify-between mt-3">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handlePreview(media)}
                      className="text-primary-600"
                    >
                      <EyeIcon className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemoveMedia(media.id)}
                      disabled={deleteMutation.isPending}
                      className="text-red-600"
                    >
                      <TrashIcon className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Preview Modal */}
      <AnimatePresence>
        {previewMedia && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
            onClick={() => setPreviewMedia(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="relative max-w-4xl max-h-[90vh] bg-white rounded-lg overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="absolute top-4 right-4 z-10">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setPreviewMedia(null)}
                  className="bg-white shadow-lg"
                >
                  <XMarkIcon className="w-5 h-5" />
                </Button>
              </div>
              
              {previewMedia.media_type === 'image' ? (
                <img 
                  src={previewMedia.url}
                  alt={previewMedia.file_name}
                  className="max-w-full max-h-[80vh] object-contain"
                />
              ) : previewMedia.media_type === 'video' ? (
                <video 
                  src={previewMedia.url}
                  controls
                  className="max-w-full max-h-[80vh]"
                />
              ) : (
                <div className="p-12 text-center">
                  <DocumentIcon className="w-24 h-24 text-gray-400 mx-auto mb-4" />
                  <p className="text-lg font-medium text-gray-900 mb-2">
                    {previewMedia.file_name}
                  </p>
                  <Button
                    variant="primary"
                    onClick={() => window.open(previewMedia.url, '_blank')}
                  >
                    Download File
                  </Button>
                </div>
              )}
              
              {previewMedia.caption && (
                <div className="p-4 border-t bg-gray-50">
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">Caption:</span> {previewMedia.caption}
                  </p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EnhancedMediaUpload;
